/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  urlPattern,
  useCurrentLocation,
  useNavigationContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";

import { assertUnreachable } from "@gnu-taler/taler-util";
import { CheckChallengeIsUpToDate } from "./components/CheckChallengeIsUpToDate.js";
import { InlineNotice, VerificationCard } from "./components/VerificationUi.js";
import { SessionId } from "./hooks/session.js";
import { AnswerChallenge } from "./pages/AnswerChallenge.js";
import { AskChallenge } from "./pages/AskChallenge.js";
import { ChallengeCompleted } from "./pages/CallengeCompleted.js";
import { Frame } from "./pages/Frame.js";
import { Setup } from "./pages/Setup.js";

export function Routing(): VNode {
  // check session and defined if this is
  // public routing or private
  return (
    <Frame>
      <PublicRouting />
    </Frame>
  );
}

const publicPages = {
  noinfo: urlPattern(/\/noinfo/, () => `#/noinfo`),
  authorize: urlPattern(/\/authorize/, () => `#/authorize`),
  ask: urlPattern(/\/ask/, () => `#/ask`),
  change: urlPattern(/\/change/, () => `#/change`),
  answer: urlPattern(/\/answer/, () => `#/answer`),
  completed: urlPattern(/\/completed/, () => `#/completed`),
  setup: urlPattern<{ client: string }>(
    /\/setup\/(?<client>[^/?#]+)/,
    ({ client }) => `#/setup/${encodeURIComponent(client)}`,
  ),
};

function safeGetParam(
  ps: Record<string, string[]>,
  n: string,
): string | undefined {
  if (!ps[n] || ps[n].length == 0) return undefined;
  return ps[n][0];
}

export function safeToURL(s: string | undefined): URL | undefined {
  if (s === undefined) return undefined;
  try {
    const url = new URL(s);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url
      : undefined;
  } catch {
    return undefined;
  }
}

export function getSession(params: Record<string, string[]>) {
  const clientId = safeGetParam(params, "client_id");
  const redirectURL = safeToURL(safeGetParam(params, "redirect_uri"));
  const state = safeGetParam(params, "state");
  const nonce = safeGetParam(params, "nonce");

  const sessionId: SessionId | undefined =
    !clientId || !redirectURL || !nonce
      ? undefined
      : {
          clientId,
          nonce: nonce,
          redirectURL: redirectURL.href,
          state,
        };
  return sessionId;
}

function PublicRouting(): VNode {
  const loc = useCurrentLocation(publicPages);
  const { i18n } = useTranslationContext();
  const { navigateTo } = useNavigationContext();
  // Translators: "attempts" are tries to enter a verification code; "address
  // changes" include changing an email address, phone number, or postal
  // address.
  const exhaustedDescription = i18n.str`No verification attempts or address changes remain.`;

  const location: typeof loc =
    loc.name === undefined
      ? {
          ...loc,
          name: "authorize",
        }
      : loc;

  switch (location.name) {
    case "noinfo": {
      // Translators: Page title shown when a verification URL does not contain
      // enough session information to identify anything to verify.
      const unavailableTitle = i18n.str`Verification information unavailable`;
      // Translators: Warning heading for a verification URL that contains no
      // usable verification request.
      const nothingToVerifyTitle = i18n.str`Nothing to verify`;
      // Translators: Recovery instruction for a verification URL with no usable
      // session information.
      const restartDescription = i18n.str`Return to the application that sent you here and start the verification again.`;
      return (
        <VerificationCard title={unavailableTitle}>
          <InlineNotice tone="warning" title={nothingToVerifyTitle}>
            {restartDescription}
          </InlineNotice>
        </VerificationCard>
      );
    }
    case "setup": {
      const secret = safeGetParam(location.params, "secret");
      const redirectURL = safeToURL(
        safeGetParam(location.params, "redirect_uri"),
      );

      return (
        <Setup
          clientId={safeDecodeURIComponent(location.values.client)}
          secret={secret}
          redirectURL={redirectURL}
        />
      );
    }
    case "authorize": {
      const sessionId = getSession(location.params);

      if (!sessionId) {
        return <MissingSessionParameters />;
      }
      return (
        <CheckChallengeIsUpToDate
          session={sessionId}
          onStatus={(nextPage) => {
            switch (nextPage) {
              case "ask":
                navigateTo(publicPages.ask.url({}));
                return;
              case "answer":
                navigateTo(publicPages.answer.url({}));
                return;
              case "completed":
                navigateTo(publicPages.completed.url({}));
                return;
              case "exhausted":
                return;
              default:
                assertUnreachable(nextPage);
            }
          }}
        >
          {exhaustedDescription}
        </CheckChallengeIsUpToDate>
      );
    }
    case "ask": {
      const sessionId = getSession(location.params);

      if (!sessionId) {
        return <MissingSessionParameters />;
      }
      return (
        <AskChallenge
          session={sessionId}
          focus
          routeSolveChallenge={publicPages.answer}
          onSendSuccesful={() => {
            navigateTo(publicPages.answer.url({}));
          }}
          onComplete={() => {
            navigateTo(publicPages.completed.url({}));
          }}
        />
      );
    }
    case "change": {
      const sessionId = getSession(location.params);

      if (!sessionId) {
        return <MissingSessionParameters />;
      }
      return (
        <AskChallenge
          session={sessionId}
          changeOnly
          focus
          routeSolveChallenge={publicPages.answer}
          onSendSuccesful={() => {
            navigateTo(publicPages.answer.url({}));
          }}
          onComplete={() => {
            navigateTo(publicPages.completed.url({}));
          }}
        />
      );
    }
    case "answer": {
      const sessionId = getSession(location.params);

      if (!sessionId) {
        return <MissingSessionParameters />;
      }

      return (
        <AnswerChallenge
          focus
          session={sessionId}
          routeAsk={publicPages.change}
          onComplete={() => {
            navigateTo(publicPages.completed.url({}));
          }}
        />
      );
    }
    case "completed": {
      const sessionId = getSession(location.params);
      if (!sessionId) return <MissingSessionParameters />;
      return <ChallengeCompleted session={sessionId} />;
    }
    default:
      assertUnreachable(location);
  }
}

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function MissingSessionParameters(): VNode {
  const { i18n } = useTranslationContext();
  // Translators: Page title for a malformed verification URL missing required
  // Challenger session parameters.
  const invalidLinkTitle = i18n.str`Invalid verification link`;
  // Translators: Error-notice heading when a verification URL omits required
  // session parameters.
  const missingInformationTitle = i18n.str`Required information is missing`;
  // Translators: Recovery instruction for a malformed verification URL.
  const restartDescription = i18n.str`Return to the application that sent you here and start the verification again.`;
  return (
    <VerificationCard title={invalidLinkTitle}>
      <InlineNotice tone="error" title={missingInformationTitle}>
        {restartDescription}
      </InlineNotice>
    </VerificationCard>
  );
}
